import * as z from 'zod'
import {
    DIssueListByCompanyItem,
    DIssueListByShareItem,
    DIssueListItem,
    DIssueSingleItem,
    IssueListByCompanyItem,
    IssueListByShareItem,
    IssueListItem,
    IssueSingleItem,
} from '../IssueEntity.js'
import { DeleteApiContract, GetApiContract, PatchApiContract, PostApiContract, PutApiContract } from './ApiContract.js'
import { schemaBoolish } from './zodUtils.js'
import { advisorSchema, feePutSchema, linkedIssueSchema } from './utils.js'
import { putSourceSchema } from './SourceApiContract.js'
import { putInvestorSchema } from './InvestorsApiContract.js'
import { ISSUE_TYPES } from '../IssueType.js'
import { IssueAdvisorType } from '../AdvisorEntity.js'

export interface GetIssueSingleItemRequest extends GetApiContract {
    Response: DIssueSingleItem
    ResponseJson: IssueSingleItem
    QueryParameters: {
        preferredLanguage?: 'sv' | 'en'
        /** When true, investor amounts are returned as stored (no derived cash ↔ %). */
        rawInvestorAmounts?: 'true'
    }
}

export interface GetIssueByCompanyRequest extends GetApiContract {
    Response: Array<DIssueListByCompanyItem>
    ResponseJson: Array<IssueListByCompanyItem>
    QueryParameters: {
        companyId: string
        excludeCancelled?: 'true'
    }
}

export interface GetIssueByShareRequest extends GetApiContract {
    Response: Array<DIssueListByShareItem>
    ResponseJson: Array<IssueListByShareItem>
    QueryParameters: {
        shareId: string
    }
}

export interface GetIssueListRequest extends GetApiContract {
    Response: Array<DIssueListItem>
    ResponseJson: Array<IssueListItem>
}

export const putIssueSchema = z.intersection(
    z.object({
        abbTarget: z.coerce.number().nullish(),
        earnout: z.coerce.number().nullish(),
        exRightsDate: z.iso.date().nullish(),
        externalComment: z.string().nullish(),
        generalMeetingDate: z.iso.date().nullish(),
        id: z.uuid(),
        incRightsDate: z.iso.date().nullish(),
        internalComment: z.string().nullish(),
        investorsAfterProspectus: z.coerce.number().nullish(),
        investorsAfterTerms: z.coerce.number().nullish(),
        ipoIntervalHigh: z.union([z.string(), z.number()]).nullish(),
        ipoIntervalLow: z.union([z.string(), z.number()]).nullish(),
        ipoTradingDate: z.iso.date().nullish(),
        isinRights: z.string().nullish(),
        isinUnits: z.string().nullish(),
        minimumSubCondition: z.union([z.string(), z.number()]).nullish(),
        noFinancialAdvisor: schemaBoolish.nullish(),
        noGuarantors: schemaBoolish.nullish(),
        noInvestorsNamed: schemaBoolish.nullish(),
        noIsinUnits: schemaBoolish.nullish(),
        noIssuingAgent: schemaBoolish.nullish(),
        noLegalAdvisor: schemaBoolish.nullish(),
        noncashConsideration: z.string().nullish(),
        noOverAllotment: schemaBoolish.nullish(),
        noParticipantsNamed: schemaBoolish.nullish(),
        noSecondaryOffering: schemaBoolish.nullish(),
        noTermsInitially: schemaBoolish.nullish(),
        offeredUnits: z.coerce.number().nullish(),
        offeredUnitsUnlisted: z.coerce.number().nullish(),
        outstandingShares: z.coerce.number().nullish(),
        overAllotmentAllocation: z.coerce.number().nullish(),
        overAllotmentAllocationSecondary: z.coerce.number().nullish(),
        overAllotmentSize: z.coerce.number().nullish(),
        overAllotmentSizeSecondary: z.coerce.number().nullish(),
        ownShares: schemaBoolish.optional(),
        publishedAt: z.iso
            .datetime()
            .nullish()
            .transform<Date | null | undefined>((d) => {
                if (typeof d !== 'string') return d

                return new Date(d)
            }),
        recordDate: z.iso.date().nullish(),
        rightsEndDate: z.iso.date().nullish(),
        rightsReceived: z.coerce.number().nullish(),
        rightsRequired: z.coerce.number().nullish(),
        rightsStartDate: z.iso.date().nullish(),
        rto: schemaBoolish.nullish(),
        secondaryOffering: z.coerce.number().nullish(),
        secondarySub: z.coerce.number().nullish(),
        shareId: z.string().optional(),
        sharesInUnit: z.coerce.number().nullish(),
        sharesRequired: z.coerce.number().nullish(),
        spac: schemaBoolish.nullish(),
        statusComplete: schemaBoolish.nullish(),
        statusHideUntil: z.iso.datetime().nullish(),
        statusMissingFields: z.string().nullish(),
        statusStage: z.number().nullish(),
        subEndDate: z.iso.date().nullish(),
        subStartDate: z.iso.date().nullish(),
        unitExtraContent: z.string().nullish(),
        unitPctSubGuarantor: z.union([z.string(), z.number()]).nullish(),
        unitPctSubWithoutRights: z.union([z.string(), z.number()]).nullish(),
        unitPctSubWithRights: z.union([z.string(), z.number()]).nullish(),
        unitsReceived: z.coerce.number().nullish(),
        unitSubGuarantor: z.coerce.number().nullish(),
        unitSubPrice: z.union([z.string(), z.number()]).nullish(),
        unitSubTotal: z.coerce.number().nullish(),
        unitSubWithoutRights: z.coerce.number().nullish(),
        unitSubWithRights: z.coerce.number().nullish(),

        advisors: z.array(
            advisorSchema.extend({
                type: z.enum(['financial_advisor', 'legal_advisor', 'issuing_agent'] as Array<IssueAdvisorType>),
            }),
        ),
        fees: z.array(feePutSchema),
        linkedIssues: z.array(linkedIssueSchema),
        sources: z.array(putSourceSchema),
        investors: z.array(putInvestorSchema),
    }),
    z.discriminatedUnion('type', [
        z.object({
            type: z.union(ISSUE_TYPES.filter((t) => t !== 'IPO').map((t) => z.literal(t))),
        }),
        z.object({
            type: z.literal('IPO'),
            ipoData: z.object({
                shareId: z.string(),
                companyId: z.string().nullish(),
                instrumentId: z.string().nullish(),
                countryCode: z.string().nullish(),
                currency: z.string().nullish(),
                companyInstrumentIds: z.array(z.string()).nullish(),
                companyShareIds: z.array(z.string()).nullish(),
                issueType: z.literal('IPO'),
                instrumentName: z.string().nullish(),
                instrumentIsin: z.string().nullish(),
                instrumentSymbol: z.string().nullish(),
                instrumentMillistreamInsref: z.coerce.number().nullish(),
                marketplaceId: z.string().nullish(),
                companyName: z.string().nullish(),
                companyTotalShares: z.coerce.number().nullish(),
                shareNumberOfShares: z.coerce.number().nullish(),
            }),
        }),
    ]),
)

export interface PutIssueRequest extends PutApiContract {
    Body: z.infer<typeof putIssueSchema>
    Response: DIssueSingleItem
    ResponseJson: IssueSingleItem
}

export const postIssueSchema = z.union([
    z.object({
        shareId: z.string(),
        type: z.union(ISSUE_TYPES.map((t) => z.literal(t))),
        ipoData: z.undefined().optional(),
    }),
    z.object({
        type: z.literal('IPO'),
        ipoData: z.object({ instrumentName: z.string() }),
    }),
])

export interface PostIssueRequest extends PostApiContract {
    Body: z.infer<typeof postIssueSchema>
    Response: DIssueSingleItem
    ResponseJson: IssueSingleItem
}

export const patchIssueSchema = z.union([
    z.object({
        matchedTo: z.string(),
    }),
    z.object({
        isinUnits: z.string().nullish(),
        isinRights: z.string().nullish(),
    }),
])

export interface PatchIssueRequest extends PatchApiContract {
    Body: z.infer<typeof patchIssueSchema>
    Response: DIssueSingleItem
    ResponseJson: IssueSingleItem
}

export interface DeleteIssueRequest extends DeleteApiContract {
    Response: {
        message: string
    }
    ResponseJson: {
        message: string
    }
}
